import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress, OrgContact } from '~/types'

type ContactsResponse = {
  contacts: OrgContact[]
}

const getOrgContactsQueryKey = (activeOrgId: string | undefined) => [
  QUERY_KEYS.ORG_CONTACTS,
  activeOrgId,
]

// Popovers mount this on every open; a minute avoids a refetch per open.
const CONTACTS_STALE_TIME = 60_000

const EMPTY_CONTACTS: OrgContact[] = []

export const useOrgContacts = (): {
  contacts: OrgContact[]
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  const { data, isLoading } = useQuery({
    queryKey: getOrgContactsQueryKey(activeOrgId),
    queryFn: async (): Promise<ContactsResponse> => {
      if (!activeOrgId) throw new Error('No orgId')

      const response = await serverGetRequest<ContactsResponse>({
        path: '/api/org/contacts/list',
        params: { orgId: activeOrgId },
      })
      if (!response) throw new Error('Failed to fetch contacts')

      return response.data
    },
    enabled: !!activeOrgId,
    staleTime: CONTACTS_STALE_TIME,
  })

  return { contacts: data?.contacts ?? EMPTY_CONTACTS, isLoading }
}

export const useOrgContactsUpdates = () => {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const upsertContact = useCallback(
    (contact: OrgContact) => {
      queryClient.setQueryData<ContactsResponse>(
        getOrgContactsQueryKey(activeOrgId),
        (data) =>
          data
            ? produce(data, (draft) => {
                const index = draft.contacts.findIndex(
                  (existing) => existing.address === contact.address,
                )
                if (index === -1) draft.contacts.push(contact)
                else draft.contacts[index] = contact
              })
            : data,
      )
    },
    [queryClient, activeOrgId],
  )

  const removeContact = useCallback(
    (address: IAddress) => {
      queryClient.setQueryData<ContactsResponse>(
        getOrgContactsQueryKey(activeOrgId),
        (data) =>
          data
            ? produce(data, (draft) => {
                draft.contacts = draft.contacts.filter(
                  (contact) => contact.address !== address,
                )
              })
            : data,
      )
    },
    [queryClient, activeOrgId],
  )

  return { upsertContact, removeContact }
}
